/* eslint-disable no-console */
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { NOTIFICATIONS_FETCH_INTERVAL } from 'api/constants';
import { fetchNotificationsCounts } from 'api/endpoints/notifications';
import store from 'store';

import type { Dispatch } from 'store/store.types';

/**
 * Hook that polls for notification counts on an interval and stores them in Redux.
 * The session-timeout and recovery-request warnings read those counts through
 * useSessionTimeoutWarning and usePendingRecoveryRequests. App.tsx mounts the
 * warnings once per page.
 */
const useNotificationsPoller = () => {
  const dispatch = useDispatch<Dispatch>();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getUser = () => store.getState().auth.user;

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return stopPolling;
  }, [stopPolling]);

  const fetchNotifications = async () => {
    try {
      const user = getUser();
      if (isEmpty(user)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        return;
      }

      const { data } = await fetchNotificationsCounts({
        query: { ownProfiles: true },
      });

      dispatch.auth.setNotificationsCounts(data);
    } catch (err: unknown) {
      const error = err as { data?: { error?: string } };
      console.error(
        error.data?.error ||
          `Failed fetching notifications counts: ${JSON.stringify(err)}`,
      );
    }
  };

  const startPolling = () => {
    if (getUser()) {
      // Clear any existing interval before starting a new one
      stopPolling();
      fetchNotifications();
      intervalRef.current = setInterval(() => {
        fetchNotifications();
      }, NOTIFICATIONS_FETCH_INTERVAL);
    }
  };

  return {
    startPolling,
    stopPolling,
    fetchNotifications,
  };
};

export default useNotificationsPoller;
